import React from 'react';
import { useLanguage } from '@/LanguageContext';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';

export const Footer = () => {
  const { t } = useLanguage();

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const quickLinks = [
    { label: t('nav.about'), href: '#about' },
    { label: t('nav.products'), href: '#products' },
    { label: t('nav.whyUs'), href: '#why-us' },
    { label: t('nav.compliance'), href: '#compliance' },
    { label: t('nav.faq'), href: '#faq' },
    { label: t('nav.contact'), href: '#contact' },
  ];

  const categoryLinks = [
    "Infusion Therapy",
    "Urology",
    "Dialysis",
    "Central Venous Catheters",
    "Gastroenterology",
    "Blood Collection Tubes",
    "Anaesthesia",
    "Surgery & Wound Drainage",
    "Blood Management",
  ];

  return (
    <footer data-testid="footer-section" className="bg-[#0A1628] py-24 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.65\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          <div className="lg:col-span-1">
            <h3 className="text-white font-bold text-xl tracking-tight mb-6">
              <span className="text-[#C5A55A]">Medecine</span> Supply S.A.
            </h3>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              {t('footer.description')}
            </p>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm tracking-wider uppercase mb-6">{t('footer.quickLinks')}</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, i) => (
                <li key={i}>
                  <a
                    href={link.href}
                    onClick={(e) => { e.preventDefault(); scrollTo(link.href); }}
                    className="text-slate-400 text-sm hover:text-[#C5A55A] transition-colors duration-300"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm tracking-wider uppercase mb-6">{t('footer.categories')}</h4>
            <ul className="space-y-3">
              {categoryLinks.map((cat, i) => (
                <li key={i}>
                  <span className="text-slate-400 text-sm hover:text-[#C5A55A] transition-colors duration-300 cursor-pointer">
                    {cat}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm tracking-wider uppercase mb-6">{t('footer.contactInfo')}</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-[#C5A55A] flex-shrink-0 mt-0.5" />
                <a href="mailto:info@medecinesupply.com" className="text-slate-400 text-sm hover:text-[#C5A55A] transition-colors">
                  {t('footer.email')}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-[#C5A55A] flex-shrink-0 mt-0.5" />
                <span className="text-slate-400 text-sm">{t('footer.phone')}</span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#C5A55A] flex-shrink-0 mt-0.5" />
                <span className="text-slate-400 text-sm">{t('footer.address')}</span>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-[#C5A55A] flex-shrink-0 mt-0.5" />
                <span className="text-slate-400 text-sm">{t('footer.hours')}</span>
              </li>
            </ul>

            <div className="mt-8">
              <h4 className="text-white font-semibold text-sm tracking-wider uppercase mb-4">{t('footer.legal')}</h4>
              <ul className="space-y-2">
                <li><span className="text-slate-400 text-sm hover:text-[#C5A55A] transition-colors cursor-pointer">{t('footer.privacy')}</span></li>
                <li><span className="text-slate-400 text-sm hover:text-[#C5A55A] transition-colors cursor-pointer">{t('footer.terms')}</span></li>
                <li><span className="text-slate-400 text-sm hover:text-[#C5A55A] transition-colors cursor-pointer">{t('footer.refund')}</span></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            &copy; {new Date().getFullYear()} {t('footer.copyright')}
          </p>
          <p className="text-slate-600 text-xs">
            FDA Compliant &middot; ISO 13485 &middot; CE Marked &middot; GMP Certified
          </p>
        </div>
      </div>
    </footer>
  );
};
